import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Logo12EN12 from './Logo12EN12';
import { hoyLocal } from '../lib/horaEspana';

/*
 * LA FRASE DEL DÍA, AL ENTRAR (15-10). El 14-10 se quitó del Inicio; vuelve, pero sola, a
 * pantalla completa nada más iniciar sesión, y se desvanece sobre el Inicio. Solo al iniciar
 * sesión: ni al recargar ni al volver a la pestaña. Por eso la pide la pantalla de entrar con
 * una marca de esta visita (sessionStorage) y aquí se consume en cuanto se lee.
 *
 * La frase es la de siempre (`frase_del_dia` de /settings/app, con su cola y su rotación) y
 * obedece al mismo interruptor del panel. Si está apagado o no hay frase, no sale nada.
 *
 * EL DISEÑO DEL HANDOFF «SPLASH CON CITA» (16-10): el logo arriba a la izquierda; en medio
 * «HOY · DÍA DE ENTRENO», la cita en grande y una raya naranja; abajo «Hola, {nombre}» y una
 * barra que se llena mientras dura. Entra por partes (logo y etiqueta, la cita subiendo, luego
 * el resto) y sale con un fundido. Tocar la salta. Con «reducir movimiento», solo fundidos.
 *
 * Lo que NO se ha copiado del handoff: sus citas fijas por tipo de día y la racha (la frase
 * sigue siendo la del servidor) y salir «una vez al día» (sigue siendo al iniciar sesión).
 *
 * La cita va entre comillas y en cursiva (Enrique, 16-10).
 */

const MARCA = 'frase-al-entrar';
// Un segundo más que los 3,2 s del handoff (Enrique, 16-10): con la cita entrando a los
// 400 ms no daba tiempo a leerla entera.
const DURACION_MS = 4200;   // lo que tarda la barra en llenarse
const FUNDIDO_MS = 700;     // la salida
const ESPERA_MAX_MS = 2500; // si los ajustes no llegan en este tiempo, se entra sin frase
const ESPERA_DIA_MS = 1200; // lo que se espera al tipo de día; si no llega, la etiqueta dice «HOY»

export const pedirFraseAlEntrar = () => {
    try { sessionStorage.setItem(MARCA, '1'); } catch { /* modo privado: se entra sin frase */ }
};

// Se lee aquí y se borra en un efecto: en desarrollo, StrictMode llama dos veces al
// inicializador del estado, y si la primera la borrara la segunda ya no la vería.
const tocaFrase = () => {
    try { return sessionStorage.getItem(MARCA) === '1'; } catch { return false; }
};

// Sin punto final, como se pintaba en el Inicio (punto 177 del 27-08): es una frase suelta,
// no un párrafo. Los suspensivos, la interrogación y la exclamación se quedan.
const sinPuntoFinal = (texto) => {
    const t = String(texto || '').trim();
    if (!t.endsWith('.') || t.endsWith('..')) return t;
    return t.slice(0, -1);
};

// El handoff pide 56 px para citas de unas diez palabras. Las del servidor las escribe gente y
// algunas son más largas: el cuerpo baja con el largo para que quepan sin desbordar.
const cuerpoDeLaCita = (texto) => (texto.length <= 60 ? 'text-[56px]'
    : texto.length <= 110 ? 'text-[44px]' : 'text-[34px]');

const sinMovimiento = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

const FraseAlEntrar = () => {
    const { appSettings, pantalla, user, api } = useAuth();
    // 'esperando' tapa el Inicio mientras llegan los datos, para que no asome y luego salte
    // la frase encima; 'frase' la enseña; 'fundido' la desvanece; 'fuera' desmonta.
    const [fase, setFase] = useState(() => (tocaFrase() ? 'esperando' : 'fuera'));
    // 0 nada · 1 logo, etiqueta y barra · 2 la cita · 3 la raya y el saludo (tiempos del handoff)
    const [paso, setPaso] = useState(0);
    const [tipoDia, setTipoDia] = useState(undefined);   // undefined: aún no se sabe
    const quieto = useRef(sinMovimiento()).current;

    useEffect(() => {
        try { sessionStorage.removeItem(MARCA); } catch { /* nada que borrar */ }
    }, []);

    // El tipo de hoy para la etiqueta: el del día guardado y, si no hay, el previsto. Es el
    // mismo `GET /diets/{hoy}` que pide el Inicio.
    useEffect(() => {
        if (fase !== 'esperando' || !api) return undefined;
        let vivo = true;
        api.get(`/diets/${hoyLocal()}`)
            .then(({ data }) => {
                const t = data?.tipo_dia || data?.tipo_dia_previsto || null;
                if (vivo) setTipoDia(t ? (t === 'descanso' ? 'descanso' : 'entreno') : null);
            })
            .catch(() => { if (vivo) setTipoDia(null); });
        const t = setTimeout(() => { if (vivo) setTipoDia((v) => (v === undefined ? null : v)); }, ESPERA_DIA_MS);
        return () => { vivo = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- se pide una vez, al montar
    }, []);

    const texto = appSettings && pantalla('frase_del_dia')
        ? sinPuntoFinal(appSettings?.frase_del_dia?.texto)
        : '';

    useEffect(() => {
        if (fase !== 'esperando') return undefined;
        if (appSettings && !texto) { setFase('fundido'); return undefined; }
        if (appSettings && tipoDia !== undefined) { setFase('frase'); return undefined; }
        const t = setTimeout(() => setFase('fundido'), ESPERA_MAX_MS);
        return () => clearTimeout(t);
    }, [fase, appSettings, texto, tipoDia]);

    useEffect(() => {
        if (fase === 'frase') {
            const ts = [
                setTimeout(() => setPaso(1), 60),
                setTimeout(() => setPaso(2), 400),
                setTimeout(() => setPaso(3), 1100),
                setTimeout(() => setFase('fundido'), 60 + DURACION_MS),
            ];
            return () => ts.forEach(clearTimeout);
        }
        if (fase === 'fundido') {
            const t = setTimeout(() => setFase('fuera'), FUNDIDO_MS);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [fase]);

    if (fase === 'fuera') return null;

    const nombre = (user?.name || '').trim().split(/\s+/)[0];
    const etiqueta = tipoDia === 'descanso' ? 'Hoy · día de descanso'
        : tipoDia === 'entreno' ? 'Hoy · día de entreno' : 'Hoy';
    const aparece = (desde, ms) => ({
        opacity: paso >= desde ? 1 : 0,
        transition: `opacity ${ms}ms ease-out`,
    });

    return (
        <div
            className="fixed inset-0 z-[100] bg-[#121212] text-[#f3f2ef] flex flex-col cursor-pointer select-none"
            style={{
                opacity: fase === 'fundido' ? 0 : 1,
                transition: `opacity ${FUNDIDO_MS}ms ease-in-out`,
                paddingTop: 'calc(64px + env(safe-area-inset-top))',
                paddingBottom: 'calc(48px + env(safe-area-inset-bottom))',
                paddingLeft: 'calc(32px + env(safe-area-inset-left))',
                paddingRight: 'calc(32px + env(safe-area-inset-right))',
            }}
            onClick={() => setFase('fundido')}
            role="presentation"
            data-testid="frase-al-entrar"
        >
            {texto && (
                <>
                    <div style={aparece(1, 500)}>
                        <Logo12EN12 size="sm" tone="dark" />
                    </div>

                    <figure className="flex-1 flex flex-col justify-center gap-6 max-w-xl">
                        <p className="text-xs font-bold uppercase tracking-[2px] text-brand" style={aparece(1, 500)}
                            data-testid="frase-etiqueta">
                            {etiqueta}
                        </p>
                        <blockquote
                            className={`font-heading font-bold italic leading-[0.96] tracking-[-0.5px] [text-wrap:balance] break-words ${cuerpoDeLaCita(texto)}`}
                            style={{
                                opacity: paso >= 2 ? 1 : 0,
                                transform: quieto || paso >= 2 ? 'translateY(0)' : 'translateY(16px)',
                                transition: 'opacity 800ms ease-out, transform 900ms cubic-bezier(.2,.7,.2,1)',
                            }}>
                            «{texto}»
                        </blockquote>
                        <span aria-hidden="true" className="block w-10 h-[3px] bg-brand" style={aparece(3, 600)} />
                    </figure>

                    <div className="flex flex-col gap-2.5" style={aparece(3, 600)}>
                        {nombre && <p className="text-[15px] font-semibold">Hola, {nombre}</p>}
                        <div className="h-[3px] rounded-full bg-[#2c2c2c] overflow-hidden">
                            <div className="h-full bg-[#f3f2ef]"
                                style={{
                                    width: paso >= 1 ? '100%' : '0%',
                                    transition: paso >= 1 ? `width ${DURACION_MS}ms linear` : 'none',
                                }} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FraseAlEntrar;
